import { Button } from "@admin/components/atoms/Button";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { SelectPersonnel } from "@admin/components/molecules/Person";
import {
  getLocationUsers,
  LocationUser,
  Shift,
  updateShift,
} from "@enos-monorepo/lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface AssingOpenShiftModalProps extends ModalProps {
  companyId: string;
  locationId: string;
  shift?: Shift;
}

export function AssingOpenShiftModal({
  companyId,
  locationId,
  shift,
  ...props
}: AssingOpenShiftModalProps) {
  const { t } = useTranslation();
  const [selectedPersonel, setSelectedPersonel] = useState<LocationUser>();
  const queryClient = useQueryClient();

  const { data: locationUsers } = useQuery<LocationUser[]>({
    queryKey: ["companyLocations", locationId],
    queryFn: () => getLocationUsers(locationId ?? ""),
    enabled: !!locationId,
  });

  const mutation = useMutation({
    mutationFn: updateShift,
    onSuccess: () => {
      toast.success(t("shift.The shift has been assigned"));
      queryClient.invalidateQueries({ queryKey: ["shifts", locationId] });
      props.onClose?.();
    },
    onError: () => {
      toast.error(t("shift.Unable to assign the shift"));
    },
  });

  const handleSubmit = () => {
    if (!selectedPersonel || !shift || !shift.id) return;

    mutation.mutate({
      companyId,
      companyLocationId: locationId,
      id: shift.id,
      userId: selectedPersonel.userId,
    });
  };

  return (
    <Modal
      title={t("shift.Assign the Open Shift to a Personnel")}
      actionButtons={
        <>
          <Button variant="secondary" type="button" onClick={props.onClose}>
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            {t("shift.Assign")}
          </Button>
        </>
      }
      {...props}
    >
      <Label title={t("shift.Appoint to a Personnel")}>
        {locationUsers && (
          <SelectPersonnel
            personnels={locationUsers.filter((user) => !user.isDeleted)}
            onSelectPersonnel={setSelectedPersonel}
          />
        )}
      </Label>
    </Modal>
  );
}
